import { type RefObject, useEffect, useMemo } from "react";

import { type DraftParagraphLockEnum } from "@app_schema";

import { type ParagraphLook } from "@styled/paragraph_editor";

import { FILL_CLASS, SLOT_CLASS } from "./draft_fill";
import { HEADER_TIP, LOCK_TIPS, WRITING_TIP } from "./draft_lock_tips";
import { drawnBlocks } from "./insert_gap";

export type Tip = { text: string; top: number; left: number };
// His paragraph, by block, or a line of a drafted section, by its section's
// marker and its place in it.
export type EditTarget = { block: number } | { marker: number; line: number };
export type Opened = EditTarget & { caret: number; look: ParagraphLook };

const HEADERS = "header, footer";
// Each block's pointer, set in the Report's own document.
const POINTER_STYLE = `[data-wf-edit],.${FILL_CLASS}{cursor:text!important}[data-wf-lock]{cursor:not-allowed!important}`;
const FILLED_LINE = `.${FILL_CLASS}[data-wf-line]`;

// Where something drawn in the Report's frame is, in the window, and the
// room over it: to what's drawn above it, or the frame's top.
export const spotIn = (frame: HTMLIFrameElement, element: Element) => {
  const outer = frame.getBoundingClientRect();
  const rect = element.getBoundingClientRect();
  const above = element.previousElementSibling?.getBoundingClientRect().bottom;
  return {
    top: outer.top + rect.top,
    left: outer.left + rect.left,
    width: rect.width,
    room: rect.top - Math.max(above ?? 0, 0),
  };
};

// The paragraph a drafted line fills, drawn in the Report.
const filledLine = (root: Element, marker: number, line: number) =>
  root
    .querySelector(
      `${FILLED_LINE}[data-wf-marker="${String(marker)}"][data-wf-line="${String(line)}"]`,
    )
    ?.closest("p") ?? null;

// What he's editing, drawn in the Report: his block's first piece, or the
// paragraph his drafted line fills.
export const targetElement = (root: Element, target: EditTarget) =>
  "block" in target
    ? (drawnBlocks(root).find((block) => block.index === target.block)
        ?.element ?? null)
    : filledLine(root, target.marker, target.line);

// A section's block says it's being written while the assistant writes it.
const tipOf = (
  element: Element,
  lock: DraftParagraphLockEnum,
  writing: number | null,
) => {
  const slot = element.querySelector<HTMLElement>(`.${SLOT_CLASS}`);
  if (writing === null || !slot) return LOCK_TIPS[lock];
  return Number(slot.dataset.wfMarker) === writing
    ? WRITING_TIP
    : LOCK_TIPS[lock];
};

// How many characters into the block he clicked.
const caretAt = (doc: Document, element: Element, x: number, y: number) => {
  const point = doc.caretRangeFromPoint?.(x, y);
  if (!point || !element.contains(point.startContainer)) return 0;
  const range = doc.createRange();
  range.setStart(element, 0);
  range.setEnd(point.startContainer, point.startOffset);
  return range.toString().length;
};

const lookOf = (element: Element): ParagraphLook => {
  const text =
    [...element.querySelectorAll("span")].find(
      (span) => span.textContent.trim() !== "",
    ) ?? element;
  const style = getComputedStyle(text);
  return {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    lineHeight: style.lineHeight,
    color: style.color,
    height: element.getBoundingClientRect().height,
  };
};

// Live editing in the drawn Report: a click on his paragraph or a drafted line
// opens it where he clicked; a part that stays as it is (the section being
// written among them) says why on hover and click, with a not-allowed pointer.
export const useDraftBlockClicks = ({
  frameRef,
  root,
  drawn,
  enabled,
  locks,
  writing,
  onEdit,
  onTip,
}: {
  frameRef: RefObject<HTMLIFrameElement | null>;
  root: Element | null;
  drawn: unknown;
  enabled: boolean;
  locks: ReadonlyArray<{ block: number; lock: DraftParagraphLockEnum }>;
  writing: number | null;
  onEdit(opened: Opened): void;
  onTip(tip: Tip | null): void;
}) => {
  const lockOf = useMemo(
    () => new Map(locks.map(({ block, lock }) => [block, lock])),
    [locks],
  );

  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    const view = frame?.contentWindow;
    if (!enabled || !root || !frame || !doc || !view) return;

    const style = doc.createElement("style");
    style.textContent = POINTER_STYLE;
    doc.head.append(style);
    const blocks = drawnBlocks(root);
    blocks.forEach(({ element, index }) => {
      const lock = lockOf.get(index);
      element.toggleAttribute("data-wf-edit", !lock);
      element.toggleAttribute("data-wf-lock", Boolean(lock));
    });
    root
      .querySelectorAll(HEADERS)
      .forEach((element) => element.setAttribute("data-wf-lock", ""));

    // Why the part under the pointer stays as it is, and that part; null over
    // his own text.
    const tipAt = (event: MouseEvent): { tip: Tip; over: Element } | null => {
      const target = event.target as Element | null;
      if (target?.nodeType !== Node.ELEMENT_NODE) return null;
      const outer = frame.getBoundingClientRect();
      const at = {
        top: outer.top + event.clientY + 14,
        left: outer.left + event.clientX,
      };
      const header = target.closest(HEADERS);
      if (header) return { tip: { text: HEADER_TIP, ...at }, over: header };
      const line = target.closest<HTMLElement>(FILLED_LINE);
      if (line) {
        const marker = Number(line.dataset.wfMarker);
        return marker === writing
          ? { tip: { text: WRITING_TIP, ...at }, over: line }
          : null;
      }
      const block = blocks.find(({ element }) => element.contains(target));
      const lock = block && lockOf.get(block.index);
      const text = block && lock ? tipOf(block.element, lock, writing) : null;
      return block && text
        ? { tip: { text, ...at }, over: block.element }
        : null;
    };

    // A tip stays where it showed until the pointer leaves its part.
    let shown: Element | null = null;
    const show = (found: { tip: Tip; over: Element } | null) => {
      const over = found?.over ?? null;
      if (over === shown) return;
      shown = over;
      onTip(found?.tip ?? null);
    };
    let pending = 0;
    let last: MouseEvent | null = null;
    const move = (event: MouseEvent) => {
      last = event;
      if (pending) return;
      pending = view.requestAnimationFrame(() => {
        pending = 0;
        if (last) show(tipAt(last));
      });
    };
    const leave = () => show(null);
    const click = (event: MouseEvent) => {
      const found = tipAt(event);
      if (found) {
        shown = found.over;
        return onTip(found.tip);
      }
      const target = event.target as Element | null;
      if (target?.nodeType !== Node.ELEMENT_NODE) return;
      const line = target.closest<HTMLElement>(FILLED_LINE);
      if (line) {
        show(null);
        // Past a list item's bullet or number, which isn't his to edit.
        const prefix = line.firstChild;
        const skipped =
          prefix?.nodeType === Node.TEXT_NODE
            ? (prefix.textContent?.length ?? 0)
            : 0;
        onEdit({
          marker: Number(line.dataset.wfMarker),
          line: Number(line.dataset.wfLine),
          caret: Math.max(
            0,
            caretAt(doc, line, event.clientX, event.clientY) - skipped,
          ),
          look: lookOf(line.closest("p") ?? line),
        });
        return;
      }
      const block = blocks.find(({ element }) => element.contains(target));
      if (!block || lockOf.has(block.index)) return;
      show(null);
      onEdit({
        block: block.index,
        caret: caretAt(doc, block.element, event.clientX, event.clientY),
        look: lookOf(block.element),
      });
    };
    doc.addEventListener("mousemove", move);
    doc.documentElement.addEventListener("mouseleave", leave);
    doc.addEventListener("click", click);
    return () => {
      view.cancelAnimationFrame(pending);
      doc.removeEventListener("mousemove", move);
      doc.documentElement.removeEventListener("mouseleave", leave);
      doc.removeEventListener("click", click);
      style.remove();
      // The next run shows it again if the pointer's still on its part.
      if (shown) onTip(null);
    };
  }, [frameRef, root, drawn, enabled, lockOf, writing, onEdit, onTip]);
};
